import React from 'react';
import { Loader2, Tag, CheckCircle, X, CreditCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';


/** Promo code input + applied banner. */
export function PromoCard({
  promoCode, setPromoCode, promoResult, applying, onApply, onClear,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Tag className="h-4 w-4" /> Promo Code
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-[11px] text-muted-foreground italic mb-2 font-body" data-testid="promo-helper-text">
          One promo code per order.
        </p>
        {promoResult?.valid ? (
          <div className="flex items-center justify-between rounded-md border border-green-300 bg-green-50 px-3 py-2" data-testid="applied-promo-banner">
            <div className="flex items-center gap-2 text-green-800 text-sm">
              <CheckCircle className="h-4 w-4" />
              <span><strong>{promoCode.toUpperCase()}</strong> — {promoResult.discount_percent}% off</span>
            </div>
            <Button
              size="sm" variant="ghost"
              className="h-7 px-2 text-green-800 hover:text-green-900 hover:bg-green-100"
              onClick={onClear}
              data-testid="remove-promo-button"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Input
              placeholder="Enter code"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value)}
              data-testid="promo-code-input"
              className="flex-1"
            />
            <Button variant="outline" onClick={onApply} disabled={applying}>
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/** Totals + RUO/age acks + the Continue/Place Order button. */
export function PaymentSummaryCard({
  cart, totals, ageAck, setAgeAck, ruoAck, setRuoAck,
  processing, onCheckout, submitError, onResendVerification, resendingVerification,
  manualMode = false,
}) {
  const { discountAmount, shippingLabel, shippingCost, shippingIsFree, finalTotal, tipOnly } = totals;
  const ctaLabel = (() => {
    if (processing) return 'Processing...';
    if (finalTotal <= 0) return 'Place Free Order';
    if (manualMode) return 'Place Order';
    return 'Continue to Payment';
  })();
  return (
    <Card>
      <CardHeader><CardTitle className="font-heading text-lg">Payment</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span>${cart.total.toFixed(2)}</span>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount</span>
            <span>-${discountAmount.toFixed(2)}</span>
          </div>
        )}
        {!tipOnly && (
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">{shippingLabel}</span>
            <span className={shippingIsFree ? 'text-green-600 font-medium' : ''} data-testid="checkout-shipping-cost">
              {shippingIsFree ? 'FREE' : `$${shippingCost.toFixed(2)}`}
            </span>
          </div>
        )}
        <div className="flex justify-between font-semibold font-heading border-t pt-2">
          <span>Total</span>
          <span data-testid="checkout-total">${finalTotal.toFixed(2)}</span>
        </div>
        <div className="space-y-2 pt-2 border-t" data-testid="checkout-acknowledgments">
          <label className="flex items-start gap-2 text-xs text-foreground font-body cursor-pointer">
            <Checkbox checked={ageAck} onCheckedChange={(v) => setAgeAck(!!v)} className="mt-0.5" data-testid="age-acknowledgment-checkbox" />
            <span>I confirm I am at least <strong>21 years of age</strong>.</span>
          </label>
          <label className="flex items-start gap-2 text-xs text-foreground font-body cursor-pointer">
            <Checkbox checked={ruoAck} onCheckedChange={(v) => setRuoAck(!!v)} className="mt-0.5" data-testid="ruo-acknowledgment-checkbox" />
            <span>I acknowledge these products are for <strong>Research Use Only</strong> and not for human or veterinary consumption.</span>
          </label>
        </div>
        <Button
          className="w-full gap-2 bg-[hsl(210,45%,40%)] hover:bg-[hsl(210,45%,30%)] text-white"
          size="lg" onClick={onCheckout}
          disabled={processing || !ageAck || !ruoAck}
          data-testid="stripe-checkout-button"
        >
          {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
          {ctaLabel}
        </Button>
        {submitError && (
          <ErrorBanner
            submitError={submitError}
            onResendVerification={onResendVerification}
            resendingVerification={resendingVerification}
          />
        )}
      </CardContent>
    </Card>
  );
}


function ErrorBanner({ submitError, onResendVerification, resendingVerification }) {
  return (
    <div
      className={`mt-2 text-xs font-body leading-relaxed p-3 rounded-md border ${
        submitError.sentOk
          ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-rose-50 border-rose-200 text-rose-700'
      }`}
      data-testid="checkout-error-banner"
    >
      {submitError.message}
      {submitError.isUnverified && (
        <>
          {' '}
          <button
            type="button"
            onClick={onResendVerification}
            disabled={resendingVerification}
            className="underline underline-offset-2 font-semibold text-rose-800 hover:text-rose-900 disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="checkout-resend-verification-link"
          >
            {resendingVerification ? 'Sending…' : 'Resend verification email'}
          </button>
        </>
      )}
    </div>
  );
}
